import React, { FunctionComponent, useState, useEffect } from 'react';
import { Modal, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { Storage } from 'aws-amplify';
import { ToastType } from 'components/ToastContainer';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import classnames from 'classnames';
import {
  hasNoDuplicates,
  elementsAreIncludedInArray,
} from 'helpers/arrayUtils';

import { ClassProp } from 'helpers/types';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import styles from './StoreListUploadModal.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type FormData = {
  files: File[];
  fileType: string;
};

type Props = {
  classes: Styles;
  trigger: any;
  title: string;
  cardNetwork?: string;
  processType?: string;
};

const mandatoryHeaders = [
  'store_id',
  'name',
  'street',
  'suite',
  'city',
  'state',
  'postal',
  'country',
  'brands',
];

const StoreListUploadModal: FunctionComponent<Props> = ({
  trigger,
  title,
  classes,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);

  const { register, handleSubmit, errors } = useForm<FormData>();

  const errorMessage = 'File could not been uploaded';

  const sendToastMessage = (
    type: ToastType,
    message: string,
    displayMessage: string,
  ) => {
    toastMapping(
      {},
      type,
    )([
      {
        message,
        displayMessage,
      },
    ]);
  };

  const getCSVHeaders = (csvString: string) => {
    return csvString.slice(0, csvString.indexOf('\r')).split(',');
  };

  const isCSVValid = (headers: Array<string>) => {
    const allHeadersAreValid = elementsAreIncludedInArray(
      mandatoryHeaders,
      headers,
    );

    if (!allHeadersAreValid) {
      return false;
    } else {
      const hasAllMandatoryHeaders = elementsAreIncludedInArray(
        headers,
        mandatoryHeaders,
      );

      return hasNoDuplicates(headers) && hasAllMandatoryHeaders;
    }
  };

  const readUploadedFileAsText = (inputFile: Blob) => {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve) => {
      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };

      temporaryFileReader.readAsText(inputFile);
    });
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);

    const {
      files: [file],
    } = data;

    const stringCSV = (await readUploadedFileAsText(file)) as string;
    const fileHeaders: Array<string> = getCSVHeaders(stringCSV);

    const fileName = `${file.name}.stores.csv`;

    if (isCSVValid(fileHeaders)) {
      try {
        await Storage.put(`merchantStoreList/${fileName}`, file, {
          contentType: 'text/csv',
          bucket: process.env.SL_BUCKET,
          region: process.env.AWS_REGION,
        });

        setFileUploaded(true);
      } catch (err) {
        sendToastMessage('error', errorMessage, 'Error');
      }
    } else {
      sendToastMessage('error', errorMessage, 'Invalid file');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (fileUploaded) {
      sendToastMessage(
        'success',
        'File has been successufly uploaded',
        'Success',
      );
    }
  }, [fileUploaded]);

  return (
    <Modal trigger={trigger} closeIcon size="mini">
      <Header className={classes.header} icon="upload" content={title} />

      <Modal.Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.fieldGroup}>
            <input
              className={classes.file}
              name="files"
              type="file"
              accept="text/csv"
              ref={register({ required: true })}
            />
            <ErrorMessage
              errors={errors}
              name="files"
              render={({ message }: any) => (
                <span className={classes.message}>{message}</span>
              )}
            ></ErrorMessage>
          </div>

          <div className={classes.fieldGroup}>
            <input
              disabled={loading || !!errors.files}
              type="submit"
              value={loading ? 'Loading...' : 'Upload'}
              className={classnames(classes.submit, {
                [classes.disabled]: loading || !!errors.files,
              })}
            />
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};

export default withStyles(styles)(StoreListUploadModal);
